import { Button } from "@/components/ui/button";
import {
  SheetActions,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { cn, receiptInputData } from "@/utils/utils";
import {
  IconBox,
  SheetTabsContainer,
  SheetTabsContent,
  SheetTabsList,
} from "@common/components";
import { useTestExpensePolicyMutation } from "@common/utils/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { Body, Heading, Superscript } from "@hypercard/web-components";
import { CheckCircle, SpinnerGap, Warning } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { TestPolicyForm } from "./TestPolicyForm";
import FeedbackDialog from "@common/components/FeedbackDialog";
import {
  ExpenseFormSchema,
  ExpenseFormValues,
} from "@domains/reports/pages/forms/Forms";

const SheetTabs = [
  {
    name: "Manual",
    value: "manual",
  },
];

type Props = {
  onClose: () => void;
  policy: string;
};

type TestPolicyState = "form" | "success" | "error" | "loading";
export const TestPolicy = ({ policy, onClose }: Props) => {
  const { toast } = useToast();
  const [tab, setTab] = useState("manual");
  const [state, setState] = useState<TestPolicyState>("form");
  const [reason, setReason] = useState<string | null>(null);
  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(ExpenseFormSchema),
  });
  const [testExpensePolicy] = useTestExpensePolicyMutation();

  const handlePrefillForm = (formValues: Partial<ExpenseFormValues>) => {
    form.reset(formValues);
  };

  const handleSubmit = async () => {
    const { merchant, amount, date, expenseCategoryGLId, notes, receipt } =
      form.getValues();
    const [, receiptFileData] = await receiptInputData(receipt);
    setState("loading");
    try {
      const { data } = await testExpensePolicy({
        variables: {
          input: {
            policy,
            expense: {
              merchantName: merchant,
              amount: Number(amount),
              transactionDate: format(date, "yyyy-MM-dd"),
              expenseCategoryGLId:
                expenseCategoryGLId != null
                  ? parseInt(expenseCategoryGLId)
                  : undefined,
              notes,
              receiptFileData,
            },
          },
        },
      });
      if (
        data?.testExpensePolicy.__typename ===
        "MutationTestExpensePolicySuccess"
      ) {
        setReason(data.testExpensePolicy?.data?.reason ?? null);
        setState(
          data.testExpensePolicy.data.isAutoApproved ? "success" : "error",
        );
      } else {
        throw new Error();
      }
    } catch (error) {
      setState("form");
      toast({
        variant: "destructive",
        title:
          "An error occured while testing the expense policy. Please try again.",
      });
    }
  };

  const handleSubmitAnother = () => {
    if (state === "error") {
      form.reset(form.getValues());
    } else {
      form.reset({
        id: undefined,
        merchant: undefined,
        expenseCategoryGLId: undefined,
        amount: undefined,
        date: undefined,
        notes: undefined,
        receipt: undefined,
        reportId: undefined,
        attendeeEmails: undefined,
      });
    }
    setReason(null);
    setState("form");
  };

  const handleClose = () => {
    onClose();
  };

  let header = "Auto-Approved";
  let body = "This expense would have been auto-approved with the policy";
  let buttonText = "Test another expense";
  if (state === "error") {
    header = "Out of Policy";
    body =
      "This expense would have been pushed to manual review with the policy";
    buttonText = "Try again";
  }

  return (
    <SheetContent onClose={onClose}>
      {state === "form" ? (
        <>
          <SheetHeader>
            <SheetTitle>Test Policy</SheetTitle>
          </SheetHeader>
          <SheetTabsContainer value={tab} onValueChange={setTab}>
            <SheetTabsList tabs={SheetTabs} />
            <SheetTabsContent value="manual">
              <TestPolicyForm
                form={form}
                onParseReceipt={handlePrefillForm}
                onSubmit={handleSubmit}
              />
            </SheetTabsContent>
          </SheetTabsContainer>
        </>
      ) : state === "loading" ? (
        <div className="flex flex-1 flex-col items-center justify-center pb-[88px]">
          <SpinnerGap className="size-16 animate-spin" />
          <Heading size="lg" weight="regular" className="mt-6">
            Testing Policy...
          </Heading>
        </div>
      ) : (
        <>
          <div className="flex grow flex-col items-center justify-center px-sheet-gap pb-[88px]">
            <Superscript size="md">TEST EXPENSE</Superscript>
            <Heading size="lg" weight="regular" className="mt-4">
              {header}
            </Heading>
            <Body
              size="md"
              weight="regular"
              className="mt-2 text-center text-white/70"
            >
              {body}
            </Body>
            {reason && (
              <div
                className={cn(
                  "mt-6 flex w-full items-start rounded-md border p-4",
                  state === "success"
                    ? "border-success bg-success/10"
                    : "border-error bg-error/10",
                )}
              >
                <IconBox className="mr-3">
                  {state === "success" ? (
                    <CheckCircle className="size-6 text-success" />
                  ) : (
                    <Warning className="size-6 text-error" />
                  )}
                </IconBox>
                <div className="flex h-full flex-col justify-center">
                  <Body size="sm" weight="medium">
                    {reason}
                  </Body>
                </div>
              </div>
            )}
          </div>
          <SheetActions className="flex flex-col items-stretch space-y-4">
            <FeedbackDialog prefix="[From Test Policy]" />
            <Button
              onClick={handleSubmitAnother}
              variant="secondary"
              className="w-full"
            >
              {buttonText}
            </Button>
            <Button onClick={handleClose} className="w-full">
              Go back to Policy
            </Button>
          </SheetActions>
        </>
      )}
    </SheetContent>
  );
};
